/* Customer-facing compliance language.
 *
 * One owner for the commission/seller disclosure shown on the storefront
 * (footer, product detail, cart) so the three surfaces cannot drift. ShopDot,
 * Inc. is the seller and merchant of record; the selling partner operates this
 * storefront and earns a commission.
 *
 * ⛔ Do not say "merchant of record" in browsing-path copy — that phrase is
 * reserved for checkout and the order confirmation.
 */

/** The seller half of the disclosure. Shown on its own when a store turns
 *  commission disclosure off, and as the second sentence otherwise. */
const SELLER_LINE =
  'Products are sold by ShopDot, Inc., which handles payment, returns, and refunds.';

const nameOf = (sellingPartner) => String(sellingPartner || '').trim();

/** The address end customers write to about an order, a return or a refund. */
export const ORDER_SUPPORT_EMAIL = 'orders@shopdotapp.com';

/** Whether the store shows the commission sentence. Defaults to on. */
export const commissionDisclosureEnabled = (storeDetails) =>
  storeDetails?.commission_disclosure !== false &&
  storeDetails?.commissionDisclosure !== false;

/** Footer and cart disclosure. Falls back to the seller line alone when there
 *  is no selling-partner name or the store has commission disclosure off. */
export const storefrontDisclosure = (sellingPartner, enabled = true) => {
  const partner = nameOf(sellingPartner);
  const commission = partner && enabled
    ? `${partner} earns a commission when you buy through this store. `
    : '';
  return `${commission}${SELLER_LINE}`;
};

/** Product detail disclosure — the commission sentence only, and only when
 *  there is a selling partner to name. */
export const productDisclosure = (sellingPartner, enabled = true) => {
  const partner = nameOf(sellingPartner);
  if (!partner || !enabled) return '';
  return `${partner} earns a commission on products purchased through this store.`;
};

/** Order confirmation commission line. */
export const orderCommissionLine = (sellingPartner, enabled = true) => {
  const partner = nameOf(sellingPartner);
  if (!partner || !enabled) return '';
  return `${partner} earns a commission on this order.`;
};
